import React, { useEffect, useState } from "react";
import styles from "./ColorSelector.module.css";
import { Form } from "react-bootstrap";
import borderless from "../../../assets/cards/borderSample/borderless.png";
import gradiant from "../../../assets/cards/borderSample/gradiant.png";
import globalStyles from "../../../styles/globalStyles.module.css";
import { useTranslation } from "react-i18next";

interface ColorSelectorProps {
  imageSrc?: string;
  onColorChange: (color: string) => void;
  color?: string;
  showBorderless?: boolean; // New prop to control visibility of Borderless
  showGradient?: boolean; // New prop to control visibility of Borderless
  showSolid?: boolean; // New prop to control visibility of Borderless
}

const ColorSelector: React.FC<ColorSelectorProps> = ({
  imageSrc,
  onColorChange,
  color,
  showBorderless = true, // Default to true for backward compatibility
  showGradient = true, // Default to true for backward compatibility
  showSolid = true, // Default to true for backward compatibility
}) => {
  const [selectedColor, setSelectedColor] = useState<string>(
    color ?? "#000000"
  );
  const [radioValue, setRadioValue] = useState<number>(2); // Default to solid color
  // const [gradientColors, setGradientColors] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleColorSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosenColor = event.target.value; // Access the color hex from the event
    setSelectedColor(chosenColor);
    if (onColorChange) {
      onColorChange(chosenColor);
    }
  };

  const contentMap: { [key: number]: JSX.Element } = {
    1: (
      <Form.Control
        type="color"
        value={selectedColor}
        onChange={handleColorSelect}
        className="w-100 "
      />
    ),
    2: <img alt="gradiant" src={gradiant} />, // Placeholder for gradient view
    3: <img alt="borderless" src={borderless} />, // Placeholder for borderless view
  };

  useEffect(() => {
    if (radioValue === 2 && imageSrc) {
      //update gradiant
      if (!imageSrc) return;
      const img = new Image();
      img.crossOrigin = "Anonymous"; // To avoid CORS issues
      img.src = imageSrc;

      img.onload = () => {
        const tempCanvas = document.createElement("canvas");
        const ctx = tempCanvas.getContext("2d");
        if (!ctx) return;

        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);

        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const { data } = imageData;

        const colors = [];
        const numSections = 4; // Divide the image into 4 sections

        for (let section = 0; section < numSections; section++) {
          let r = 0,
            g = 0,
            b = 0,
            total = 0;

          const startX = (section * img.width) / numSections;
          const endX = ((section + 1) * img.width) / numSections;

          for (let y = 0; y < img.height; y++) {
            for (let x = startX; x < endX; x++) {
              const index = (Math.floor(y) * img.width + Math.floor(x)) * 4;

              const red = data[index];
              const green = data[index + 1];
              const blue = data[index + 2];

              // Calculate brightness
              const brightness = 0.299 * red + 0.587 * green + 0.114 * blue;

              if (brightness > 50) {
                r += red;
                g += green;
                b += blue;
                total++;
              }
            }
          }

          if (total > 0) {
            r = Math.floor(r / total);
            g = Math.floor(g / total);
            b = Math.floor(b / total);
          } else {
            r = 128;
            g = 128;
            b = 128;
          }

          colors.push(`rgb(${r}, ${g}, ${b})`);
        }

        // setGradientColors(colors);
        onColorChange(`linear-gradient(90deg, ${colors.join(", ")})`);
      };
    }

    // only rerun on imageSrc or radioValue change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, radioValue]);

  const handleRadioChange = (val: number) => {
    setRadioValue(val);

    if (val === 3) {
      // For borderless, send transparent to onColorChange
      onColorChange("transparent");
    } else if (val === 1) {
      // Solid color selected, update with the last selected color
      onColorChange(selectedColor);
    }
  };

  return (
    <Form.Group controlId="colorPicker">
      <Form.Label>{t("input.borderColor.label")}</Form.Label>

      {/* ToggleButtonGroup for selecting options */}
      <div className="d-flex justify-content-center">
        {showSolid && (
          <button
            className={`${globalStyles.button} ${
              radioValue === 1 && styles.active
            }`}
            onClick={() => handleRadioChange(1)}
          >
            {t("input.borderColor.solid")}
          </button>
        )}
        {showGradient && (
          <button
            className={`${globalStyles.button} ${
              radioValue === 2 && styles.active
            }`}
            onClick={() => handleRadioChange(2)}
          >
            {t("input.borderColor.gradient")}
          </button>
        )}
        {showBorderless && (
          <button
            className={`${globalStyles.button} ${
              radioValue === 3 && styles.active
            }`}
            onClick={() => handleRadioChange(3)}
          >
            {t("input.borderColor.borderless")}
          </button>
        )}
      </div>

      {/* Display content based on selected radio button */}
      <div className="d-flex justify-content-center m-3 mb-5">
        {contentMap[radioValue]}
      </div>
    </Form.Group>
  );
};

export default ColorSelector;
